import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMutation } from './mutation.js';
import type { Langue } from '@artosera/shared';
import { api, requeteMultipart, type AppelApi } from './client.js';
import { appelDeRequete, cleMetier, dansLaPortee, dansLaPorteeCourante } from './cles.js';
import type { Pagination } from './types.js';
export interface ArticlePresente {
  id: string;
  genre: 'article' | 'catalogue';
  titre: string;
  support: string;
  auteur: string;
  paruLe: string | null;
  url: string;
  langue: Langue;
  extrait: string;
  isbn: string;
  pages: number | null;
  expositions: string[];
  artistes: string[];
  oeuvres: string[];
  fichiers: { rang: number; nom: string; typeMime: string; octets: number; depose: string }[];
  archiveLe: string | null;
}
export type CorpsArticle = Omit<ArticlePresente, 'id' | 'fichiers' | 'archiveLe'>;
export interface FiltresPresse {
  genre?: ArticlePresente['genre'];
  expositionId?: string;
  artisteId?: string;
  oeuvreId?: string;
  q?: string;
  archives?: boolean;
  page?: number;
  parPage?: number;
}
const chemin = (g: string, id?: string) =>
  `/g/${encodeURIComponent(g)}/presse${id ? `/${encodeURIComponent(id)}` : ''}`;
export const presseApi = {
  lister: (g: string, f: FiltresPresse = {}, a?: AppelApi) => {
    const q = new URLSearchParams();
    for (const [k, v] of Object.entries(f)) if (v !== undefined) q.set(k, String(v));
    return api.get<{ articles: ArticlePresente[]; pagination: Pagination }>(`${chemin(g)}?${q}`, {
      galerie: g,
      ...a,
    });
  },
  lire: (g: string, id: string, a?: AppelApi) =>
    api.get<{ article: ArticlePresente }>(chemin(g, id), { galerie: g, ...a }),
  creer: (g: string, corps: CorpsArticle, a?: AppelApi) =>
    api.post<{ article: ArticlePresente }>(chemin(g), corps, { galerie: g, ...a }),
  modifier: (g: string, id: string, corps: Partial<CorpsArticle>, a?: AppelApi) =>
    api.patch<{ article: ArticlePresente }>(chemin(g, id), corps, { galerie: g, ...a }),
  archiver: (g: string, id: string, a?: AppelApi) =>
    api.post<{ article: ArticlePresente }>(`${chemin(g, id)}/archiver`, {}, { galerie: g, ...a }),
  deposer: (g: string, id: string, fichier: File, a?: AppelApi) => {
    const corps = new FormData();
    corps.set('fichier', fichier);
    return requeteMultipart<{ article: ArticlePresente }>(`${chemin(g, id)}/fichiers`, corps, {
      galerie: g,
      ...a,
    });
  },
  telecharger: (g: string, id: string, rang: number, a?: AppelApi) =>
    api.get<{ url: string; expireLe: string; expireDansSecondes: number; nomFichier: string }>(
      `${chemin(g, id)}/fichiers/${rang}/telechargement`,
      { galerie: g, ...a },
    ),
  retirerFichier: (g: string, id: string, rang: number, a?: AppelApi) =>
    api.delete(`${chemin(g, id)}/fichiers/${rang}`, { galerie: g, ...a }),
};
export function usePresse(g: string, f: FiltresPresse = {}) {
  return useQuery({
    queryKey: cleMetier(g, 'presse', f),
    queryFn: (c) => presseApi.lister(g, f, appelDeRequete(c)),
  });
}
export function useArticlePresse(g: string, id: string) {
  return useQuery({
    queryKey: cleMetier(g, 'presse', 'article', id),
    queryFn: (c) => presseApi.lire(g, id, appelDeRequete(c)),
    enabled: Boolean(id),
  });
}
function useMutationPresse<T, R>(g: string, mutationFn: (v: T, a: AppelApi) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn,
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: cleMetier(g, 'presse') });
      },
    }),
  );
}
export const useCreerArticle = (g: string) =>
  useMutationPresse(g, (v: CorpsArticle, a) => presseApi.creer(g, v, a));
export const useModifierArticle = (g: string) =>
  useMutationPresse(g, (v: { id: string; corps: Partial<CorpsArticle> }, a) =>
    presseApi.modifier(g, v.id, v.corps, a),
  );
export const useArchiverArticle = (g: string) =>
  useMutationPresse(g, (id: string, a) => presseApi.archiver(g, id, a));
export const useDeposerPresse = (g: string) =>
  useMutationPresse(g, (v: { id: string; fichier: File }, a) =>
    presseApi.deposer(g, v.id, v.fichier, a),
  );
export const useRetirerFichierPresse = (g: string) =>
  useMutationPresse(g, (v: { id: string; rang: number }, a) =>
    presseApi.retirerFichier(g, v.id, v.rang, a),
  );
export const telechargerPresse = (g: string, id: string, rang: number) =>
  dansLaPorteeCourante((a) => presseApi.telecharger(g, id, rang, a));
